import { useRef, useState } from 'react';

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const DraggableFloatingPreview = ({
  children,
  className,
  defaultPlacementClassName,
}: {
  children: React.ReactNode;
  className: string;
  defaultPlacementClassName: string;
}) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const getBounds = () => {
    const element = previewRef.current;
    const parent = element?.offsetParent as HTMLElement | null;
    if (!element || !parent) return null;

    return {
      element,
      elementRect: element.getBoundingClientRect(),
      parentRect: parent.getBoundingClientRect(),
    };
  };

  const clampPosition = (
    left: number,
    top: number,
    parentRect: DOMRect,
    elementRect: DOMRect,
  ) => {
    const margin = 8;
    const maxLeft = Math.max(
      margin,
      parentRect.width - elementRect.width - margin,
    );
    const maxTop = Math.max(
      margin,
      parentRect.height - elementRect.height - margin,
    );

    return {
      left: clampNumber(left, margin, maxLeft),
      top: clampNumber(top, margin, maxTop),
    };
  };

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (
    event,
  ) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const bounds = getBounds();
    if (!bounds) return;

    const currentLeft = bounds.elementRect.left - bounds.parentRect.left;
    const currentTop = bounds.elementRect.top - bounds.parentRect.top;
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - bounds.elementRect.left,
      offsetY: event.clientY - bounds.elementRect.top,
    };

    setPosition(
      clampPosition(
        currentLeft,
        currentTop,
        bounds.parentRect,
        bounds.elementRect,
      ),
    );
    setIsDragging(true);
    bounds.element.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (
    event,
  ) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId)
      return;

    const bounds = getBounds();
    if (!bounds) return;

    setPosition(
      clampPosition(
        event.clientX - bounds.parentRect.left - dragRef.current.offsetX,
        event.clientY - bounds.parentRect.top - dragRef.current.offsetY,
        bounds.parentRect,
        bounds.elementRect,
      ),
    );
  };

  const handlePointerEnd: React.PointerEventHandler<HTMLDivElement> = (
    event,
  ) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId)
      return;

    dragRef.current = null;
    setIsDragging(false);
    if (previewRef.current?.hasPointerCapture(event.pointerId)) {
      previewRef.current.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      ref={previewRef}
      className={[
        'absolute z-10 touch-none select-none cursor-grab',
        isDragging ? 'cursor-grabbing' : '',
        position ? '' : defaultPlacementClassName,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        position
          ? {
              left: position.left,
              top: position.top,
            }
          : undefined
      }
      title="Arraste para mover sua câmera"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      {children}
    </div>
  );
};

export default DraggableFloatingPreview;
